import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Clock, Loader2, ImageIcon, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Listing {
  listing_id: string;
  name: string;
  description?: string;
  duration: string;
  spots: number;
  price: number;
  time: string;
  images?: string[];
  outlet_id?: string;
}

interface Outlet {
  outlet_id: string;
  outlet_name: string;
  is_active?: boolean | null;
  slug?: string | null;
}

interface ListingsModuleProps {
  businessId?: string;
  initialOutletId?: string | null;
}

const ListingsModule = ({ businessId, initialOutletId }: ListingsModuleProps) => {
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);

  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [selectedOutletId, setSelectedOutletId] = useState<string | null>(null);

  useEffect(() => {
    const fetchListings = async () => {
      let query = supabase
        .from("listings")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");

      if (businessId) {
        query = query.eq("business_id", businessId);
      }

      const { data, error } = await query;

      if (!error && data) {
        setListings(data);
      }

      if (businessId) {
        const { data: outletsData, error: outletsError } = await supabase
          .from("outlets")
          .select("outlet_id, outlet_name, is_active, slug")
          .eq("business_id", businessId)
          .order("created_at", { ascending: true });

        if (!outletsError && outletsData) {
          const activeOutlets = outletsData.filter(o => o.is_active !== false);
          setOutlets(activeOutlets);
          if (activeOutlets.length > 0) {
            if (initialOutletId && activeOutlets.some(o => o.outlet_id === initialOutletId)) {
              setSelectedOutletId(initialOutletId);
            } else if (!selectedOutletId) {
               setSelectedOutletId(activeOutlets[0].outlet_id);
            }
          }
        }
      }

      setLoading(false);
    };

    fetchListings();
  }, [businessId, selectedOutletId, initialOutletId]);

  const filteredListings = selectedOutletId
    ? listings.filter((l) => l.outlet_id === selectedOutletId)
    : listings;

  const handleListingClick = (listing: Listing) => {
    navigate(`/listing/${listing.listing_id}`);
  };

  if (loading) {
    return (
      <section className="animate-fade-up">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </section>
    );
  }

  if (listings.length === 0) {
    return (
      <section className="animate-fade-up">
        <div className="text-center py-12 text-muted-foreground">
          No listings available at the moment.
        </div>
      </section>
    );
  }

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + "...";
  };

  return (
    <section className="animate-fade-up space-y-4">
      {outlets.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 pb-2">
          <span className="text-sm font-medium text-muted-foreground mr-1">Outlet:</span>
          {outlets.map((outlet) => (
            <Button
              key={outlet.outlet_id}
              variant={selectedOutletId === outlet.outlet_id ? "default" : "outline"}
              className="rounded-full shadow-sm"
              onClick={() => {
                if (outlet.slug) {
                  navigate(`/${outlet.slug}`);
                } else {
                  setSelectedOutletId(outlet.outlet_id);
                }
              }}
            >
              {outlet.outlet_name}
            </Button>
          ))}
        </div>
      )}

      {filteredListings.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border border-border/40 rounded-xl shadow-sm">
          No listings available for this location.
        </div>
      ) : (
        filteredListings.map((listing) => (
          <div
            key={listing.listing_id}
            onClick={() => handleListingClick(listing)}
            className="group cursor-pointer bg-card border border-border/40 rounded-xl p-4 transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-1"
          >
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
              {/* Image Placeholder - Clean rounded style */}
              <div className="w-full sm:w-[240px] flex-shrink-0 aspect-video rounded-xl relative bg-muted/60 overflow-hidden">
                {listing.images && listing.images.length > 0 ? (
                  <img
                    src={listing.images[0]}
                    alt={listing.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-muted-foreground/30 transition-transform duration-500 group-hover:scale-110" />
                    </div>
                  </>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 py-1 flex flex-col justify-center">
                <div>
                  <h3 className="font-semibold text-lg text-foreground transition-colors leading-tight">
                    {listing.name}
                  </h3>

                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-2 sm:line-clamp-3">
                    {listing.description || "Experience the ultimate way to relax and rejuvenate with our premium offering. Designed specifically with your comfort and goals in mind, this session provides an unforgettable experience that will leave you coming back for more."}
                  </p>
                </div>

                <div className="flex items-end justify-between mt-auto pt-5">
                  <div className="flex gap-6 sm:gap-10">
                    <div className="flex flex-col gap-1.5">
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium uppercase tracking-wider">
                        <Clock className="w-3.5 h-3.5" />
                        Duration
                      </span>
                      <span className="font-semibold text-foreground text-sm sm:text-base">
                        {listing.duration}
                      </span>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium uppercase tracking-wider">
                        <Tag className="w-3.5 h-3.5" />
                        Price
                      </span>
                      <span className="font-semibold text-foreground text-sm sm:text-base">
                        From ${listing.price.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <Button
                    size="sm"
                    className="font-medium h-9 px-6 rounded-full shadow-sm hover:shadow active:scale-95 transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleListingClick(listing);
                    }}
                  >
                    Book
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </section>
  );
};

export default ListingsModule;
